import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

/**
 * Polls the given query keys every `intervalMs` milliseconds while the page tab
 * is visible. Pauses automatically when the tab is hidden and resumes (with an
 * immediate refetch) when the tab becomes active again.
 *
 * This is the lightweight live-update mechanism for shared data pages like
 * Club Expenses — no WebSocket infrastructure required.
 */
export function useAutoRefresh(queryKeys, intervalMs = 20000) {
  const qc      = useQueryClient();
  const timer   = useRef(null);
  const visible = useRef(!document.hidden);

  const refetch = () => {
    for (const key of queryKeys) {
      qc.invalidateQueries({ queryKey: key });
    }
  };

  useEffect(() => {
    const start = () => {
      if (timer.current) return;
      timer.current = setInterval(() => {
        if (!document.hidden) refetch();
      }, intervalMs);
    };

    const stop = () => {
      if (timer.current) { clearInterval(timer.current); timer.current = null; }
    };

    const onVisibilityChange = () => {
      if (document.hidden) {
        stop();
      } else {
        refetch();
        start();
      }
    };

    start();
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalMs]);
}
